import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { searchOdoo } from '@adornis/odoo/search.js';

const STAGE_CACHE_TIME = 6 * 60 * 60 * 1000; // 6 hours

export interface OdooStage {
  id: number;
  name: string;
  display_name: string;
  fold?: boolean;
}

interface OdooStageCacheDoc {
  _id: string;
  key: string;
  value: string;
  expiresAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

// ensure indexes on startup
void (async () => {
  logger.info('StageService::Ensuring indexes for stage cache');
  const collection = await getRawCollection<OdooStageCacheDoc>('OdooStageCache');
  await collection.createIndex({ key: 1 }, { unique: true });
  await collection.createIndex({ expiresAt: 1 }, { expireAfterSeconds: 0 });
})();

const OPEN_STAGES_CACHE_KEY = 'helpdesk-stage:open';

function stageNameCacheKey(stageName: string): string {
  return `helpdesk-stage:name:${stageName}`;
}

export async function getOdooStageByName(stageName: string): Promise<OdooStage | null> {
  if (!stageName) return null;
  const collection = await getRawCollection<OdooStageCacheDoc>('OdooStageCache');
  const key = stageNameCacheKey(stageName);

  const cachedDoc = await collection.findOne({ key });
  if (cachedDoc?.value) {
    try {
      const parsed = JSON.parse(cachedDoc.value) as OdooStage;
      if (parsed && typeof parsed.id === 'number') return parsed;
    } catch {
      // ignore broken cache entries
    }
  }

  logger.info({ stageName }, 'StageService::Fetching stage by name from Odoo');
  const stages = await searchOdoo('helpdesk.stage', {
    domain: [[['name', '=', stageName]]],
    fields: ['id', 'name', 'display_name', 'fold'],
    context: { lang: 'de_DE' },
  });

  const stage = Array.isArray(stages) && stages.length ? (stages[0] as OdooStage) : null;

  const now = new Date();
  const expiresAt = new Date(now.getTime() + STAGE_CACHE_TIME);
  await collection.updateOne(
    { key },
    {
      $set: {
        key,
        value: JSON.stringify(stage),
        expiresAt,
        updatedAt: now,
      },
      $setOnInsert: {
        _id: A.getGloballyUniqueID(),
        createdAt: now,
      },
    },
    { upsert: true },
  );

  return stage;
}

export async function getOdooOpenStages(): Promise<OdooStage[]> {
  const collection = await getRawCollection<OdooStageCacheDoc>('OdooStageCache');

  const cachedDoc = await collection.findOne({ key: OPEN_STAGES_CACHE_KEY });
  if (cachedDoc?.value) {
    try {
      const parsed = JSON.parse(cachedDoc.value) as OdooStage[];
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // ignore broken cache entries
    }
  }

  logger.info('StageService::Fetching open stages from Odoo');
  const stages = await searchOdoo('helpdesk.stage', {
    domain: [[['fold', '=', false]]],
    fields: ['id', 'name', 'display_name', 'fold'],
    context: { lang: 'de_DE' },
  });

  const now = new Date();
  const expiresAt = new Date(now.getTime() + STAGE_CACHE_TIME);
  await collection.updateOne(
    { key: OPEN_STAGES_CACHE_KEY },
    {
      $set: {
        key: OPEN_STAGES_CACHE_KEY,
        value: JSON.stringify(stages ?? []),
        expiresAt,
        updatedAt: now,
      },
      $setOnInsert: {
        _id: A.getGloballyUniqueID(),
        createdAt: now,
      },
    },
    { upsert: true },
  );

  return stages;
}
